// Training Portal buttons. Pure: data in, HTML out.
// Words: TRAINING_PORTAL_WORDS.md (session buttons, manual close), TRAINING_SERVER_WORDS.md
// (calls 1 to 3, 8, 9), GATES_AT_CUTOVER_WORDS.md (Node 4 closes by hand on date and evidence).
// Mockup: MOCKUP_TRAINING_PORTAL_APPROVED.html (2026-10-03).
// The server checks every rule. These screens only collect the entry and show the answer.

import type { SiteSessions, TrainingSession } from "./firebase";
import { expiresOn, nodeById, requiredCorrect, type NodeId, type TrainingRecord } from "./training";
import { REOPEN_REASONS, SITE_TIME_ZONE, type FailedInSession } from "./training-server";
import { usDate, type ClassResults, type Trainee } from "./training-view";
import { esc } from "./training-screens";

// ---- Times, in site time ----

/** 2026-10-05T11:42:00Z reads 7:42 AM. */
export function siteTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { timeZone: SITE_TIME_ZONE, hour: "numeric", minute: "2-digit" });
}

/** 2026-10-05T11:42:00Z reads 10/05/2026. */
export function siteDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { timeZone: SITE_TIME_ZONE, month: "2-digit", day: "2-digit", year: "numeric" });
}

function nodeNum(nodeId: NodeId): string {
  return nodeId.replace("node", "");
}

function quizLabel(quizId: string): string {
  return `Quiz ${quizId.replace("quiz", "")}`;
}

// ---- Session card ----

export function flashNote(flash: { text: string; tone: "green" | "red" } | null): string {
  return flash ? `<div class="note ${flash.tone}" role="status">${esc(flash.text)}</div>` : "";
}

/** One line per quiz on the session card: "3 passed · 1 not passed yet", or "No attempts". */
function quizLine(q: ClassResults["quizzes"][number]): string {
  if (!q.took) return '<span class="muted">No attempts</span>';
  const parts = [`<span class="ok">${q.passed} passed</span>`];
  if (q.corrected) parts.push(`${q.corrected} corrected in class`);
  if (q.open) parts.push(`<span class="red">${q.open} not passed yet</span>`);
  return parts.join(" · ");
}

export function sessionBox(siteName: string, s: SiteSessions | null, loadError = "", results: ClassResults | null = null, site = ""): string {
  if (loadError) return `<div class="note red">Could not read sessions: ${esc(loadError)}</div>`;
  if (!s) return '<div class="box"><div class="loading">Loading sessions…</div></div>';
  const head = s.open
    ? `<div class="boxhd"><div class="icon on">ON</div><div class="nm"><b>Session open · code <span class="mono code4">${esc(s.open.sessionCode)}</span></b><small>${esc(s.open.sessionId)} · opened ${esc(siteTime(s.open.startedAt))} by ${esc(s.open.startedByName)}${
        s.open.reopens?.length ? " · reopened" : ""
      }</small></div><button class="prim" type="button" data-act="end-session">End Session</button></div>`
    : `<div class="boxhd"><div class="icon grey">—</div><div class="nm"><b>No session open</b><small>${esc(siteName)}</small></div><button class="prim" type="button" data-act="start-session">Start Session</button></div>`;
  const note = s.open
    ? `<div class="rows">
        <div class="r"><div class="k">Trainees in session</div><div class="v">${results?.trainees ?? 0}</div><div class="e"></div></div>
        ${(results?.quizzes ?? [])
          .map((q, i) => `<div class="r"><div class="k">${quizLabel(q.quizId)}</div><div class="v">${quizLine(q)}</div><div class="e">${i === 0 ? `<a class="chg" href="#/training/${esc(site)}/results/${esc(s.open!.sessionId)}">View Results</a>` : ""}</div></div>`)
          .join("")}
      </div>`
    : '<div class="rows"><div class="r"><div class="k">How it works</div><div class="v muted">Start gives a 4-character code. Trainees claim their tag and take quizzes on it. A session left open ends at midnight.</div><div class="e"></div></div></div>';
  return `<div class="box">${head}${note}</div>`;
}

export function pastSessionsBox(s: SiteSessions | null): string {
  if (!s) return "";
  const rows = s.past
    .map(
      (p) => `<div class="q ps"><span class="n code4">${esc(p.sessionCode)}</span><span class="tx">${esc(p.sessionId)}<small>Ended ${esc(siteDate(p.endedAt))} ${esc(siteTime(p.endedAt))}${p.endedReason ? ` · ${esc(p.endedReason)}` : ""} · <a href="#/training/${esc(p.siteNumber)}/results/${esc(p.sessionId)}">Results</a></small></span><button class="chg" type="button" data-act="reopen" data-id="${esc(p.sessionId)}">Reopen</button></div>`,
    )
    .join("");
  return `<h2 class="sec">Past Sessions</h2>
    <div class="box">${rows || '<div class="empty">No past sessions yet. Old sessions stay in the Sheet.</div>'}</div>
    ${rows ? '<div class="note">Reopen is for a session ended by mistake or a class that runs more than one day.</div>' : ""}`;
}

// ---- Class Results (screen 4): counts only, projector safe ----

export function classResultsScreen(session: Pick<TrainingSession, "sessionId" | "sessionCode" | "siteNumber" | "status"> | null, siteNumber: string, sessionId: string, r: ClassResults): string {
  const code = session?.sessionCode ?? "";
  const bars = r.quizzes
    .map((q) => {
      const pct = q.took ? Math.round((q.passed / q.took) * 100) : 0;
      const tone = !q.took ? "mu" : q.passed === q.took ? "ok" : "am";
      const label = q.took ? `${q.passed} / ${q.took} passed` : "No attempts";
      const sub = q.corrected ? `<small>${q.corrected} corrected in class</small>` : "";
      return `<div class="res"><div class="rl"><b>${quizLabel(q.quizId)}</b><span class="${tone}">${label}</span></div>${sub}<div class="rb"><i class="${tone === "am" ? "am" : ""}" style="width:${pct}%"></i></div></div>`;
    })
    .join("");
  return `<a class="back" href="#/training/${esc(siteNumber)}">Training</a>
    <div class="ident"><div class="badge code4">${esc(code || "—")}</div><div><h2>Class Results</h2><div class="sub">${esc(sessionId)} · ${session?.status === "open" ? "session open" : "session ended"} · ${r.trainees} ${r.trainees === 1 ? "trainee" : "trainees"}</div></div></div>
    <div class="box results"><div class="boxhd"><div class="nm"><b>By Quiz</b><small>No names · counts only</small></div></div>${bars}</div>
    <div class="note green">Projector safe. Each trainee counts once per quiz.</div>`;
}

// ---- Sheets ----

function sheet(title: string, body: string): string {
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="${esc(title)}">
    <div class="big">${esc(title)}</div>${body}</div></div>`;
}

/** End Session. A plain confirm, or the review gate when anyone failed. */
export function endSessionSheet(open: TrainingSession, failed: FailedInSession[], ticked: boolean, busy: boolean, error = ""): string {
  const list = failed
    .map(
      (f) =>
        `<div class="stat"><span>${esc(f.name)} · ${esc(quizLabel(f.quizId))}${f.correctionFinished ? " · corrected" : ""}</span><span class="bad">${f.scorePct}%</span></div>`,
    )
    .join("");
  const gate = failed.length
    ? `${list}<label class="chk"><input type="checkbox" id="end-review"${ticked ? " checked" : ""}><span>I reviewed missed-question feedback with all trainees listed above before this session closed.</span></label>`
    : "";
  const lead = failed.length
    ? `Trainees can no longer take quizzes on this code. ${failed.length === 1 ? "One failed attempt needs" : `${failed.length} failed attempts need`} your review first.`
    : "Trainees can no longer take quizzes on this code.";
  return sheet(
    `End session ${open.sessionCode}?`,
    `<p>${esc(lead)}</p>${gate}${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go" type="button" data-act="end-confirm"${(failed.length && !ticked) || busy ? " disabled" : ""}>${busy ? "Ending…" : "End Session"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Keep Open</button>`,
  );
}

/** Reopen. One of the four reasons, optional context. Same code works again. */
export function reopenSheet(past: TrainingSession, busy: boolean, error = "", reason = "", context = ""): string {
  const opts = REOPEN_REASONS.map((r) => `<option${r === reason ? " selected" : ""}>${esc(r)}</option>`).join("");
  return sheet(
    `Reopen session ${past.sessionCode}?`,
    `<p>Same code works again. Any other open session at this site ends first.</p>
    <div class="sheet-card">
      <label class="fl"><span class="lbl">Reason</span><select id="reopen-reason" class="fill"><option value=""${reason ? "" : " selected"}></option>${opts}</select></label>
      <label class="fl"><span class="lbl">Context (optional)</span><input id="reopen-context" maxlength="500" value="${esc(context)}" placeholder="Day 2 of class"></label>
    </div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go" type="button" data-act="reopen-confirm" data-id="${esc(past.sessionId)}"${busy ? " disabled" : ""}>${busy ? "Reopening…" : "Reopen Session"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}

// ---- Close by Hand ----

export interface CloseByHandEntry {
  completedOn: string;
  correct: string;
  total: string;
  evidence: string;
}

/** Close by Hand. Backup only. Node 4 takes the date and evidence, no score. */
export function closeByHandForm(
  t: Trainee,
  nodeId: NodeId,
  passMarkPct: number,
  activeCount: number,
  today: string,
  entry: CloseByHandEntry | null,
  busy: boolean,
  error = "",
): string {
  const node = nodeById(nodeId);
  const num = nodeNum(nodeId);
  const scored = node.quizId !== null;
  const e = entry ?? { completedOn: today, correct: "", total: String(activeCount || 15), evidence: "" };
  const total = Number(e.total) || activeCount || 15;
  const exp = /^\d{4}-\d{2}-\d{2}$/.test(e.completedOn) ? expiresOn(e.completedOn, nodeId) : null;
  const back = `#/training/${t.siteNumber}/${esc(t.qewId)}`;
  return `<a class="back" href="${back}">Cancel</a>
    <div class="ident"><div class="badge">N${num}</div><div class="ident-text"><h1>Close Node ${num} by Hand</h1><div class="sub">${esc(t.name)} · ${esc(t.qewId)}</div></div></div>
    <div class="note">Backup only. ${scored ? "Use for a paper quiz or when the app is down." : "Use when the two demo JSPs were done and the app did not record them."}</div>
    <form class="box" id="cbh-form" data-qew="${esc(t.qewId)}" data-node="${nodeId}" novalidate>
      <div class="boxhd"><div class="icon">${num}</div><div class="nm"><b>Node ${num} · ${esc(node.nodeName)}</b><small>${scored ? `Quiz ${num}` : "Field demonstration · no score"}</small></div></div>
      <div class="rows">
        <label class="r"><span class="k">Date</span><span class="v"><input class="fin" type="date" name="completedOn" value="${esc(e.completedOn)}" max="${today}" required></span><span class="e"></span></label>
        ${
          scored
            ? `<div class="r"><span class="k">Score</span><span class="v"><span class="score-in"><input class="fin num" type="number" name="correct" min="0" max="${total}" inputmode="numeric" value="${esc(e.correct)}" aria-label="Questions correct" required> of <input class="fin num" type="number" name="total" min="1" inputmode="numeric" value="${esc(e.total)}" aria-label="Questions on the quiz" required></span></span><span class="e"></span></div>
        <div class="r"><span class="k">Pass mark</span><span class="v">${passMarkPct}% · ${requiredCorrect(total, passMarkPct)} of ${total}</span><span class="e"></span></div>`
            : ""
        }
        <label class="r"><span class="k">Evidence</span><span class="v"><input class="fin wide" name="evidence" maxlength="500" value="${esc(e.evidence)}" placeholder="Type where the proof is kept" required><small class="fhint">Where the proof of this close is kept. For example: ${scored ? "Paper quiz, filed in the site training binder" : "Demo JSPs signed off on paper, filed at site"}.</small></span><span class="e"></span></label>
        <div class="r"><span class="k">Expires</span><span class="v" id="cbh-exp">${exp ? usDate(exp) : node.validDays === null ? "Does not expire" : "Set from the date"}</span><span class="e"></span></div>
      </div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <div class="note green">Saved as closed by hand, with your ID. ${scored ? "A score under the pass mark will not close the node." : ""}</div>
    <button class="prim" type="submit" form="cbh-form"${busy ? " disabled" : ""}>${busy ? "Closing…" : `Close Node ${num}`}</button>`;
}

// ---- Edit Completion Date ----

export function editDateForm(t: Trainee, r: TrainingRecord, today: string, value: string | null, busy: boolean, error = ""): string {
  const num = nodeNum(r.nodeId);
  const v = value ?? r.completedOn;
  const exp = /^\d{4}-\d{2}-\d{2}$/.test(v) ? expiresOn(v, r.nodeId) : null;
  return `<a class="back" href="#/training/${t.siteNumber}/${esc(t.qewId)}">Cancel</a>
    <div class="ident"><div class="badge">N${num}</div><div class="ident-text"><h1>Edit Completion Date</h1><div class="sub">${esc(t.name)} · Node ${num} · ${esc(r.nodeName)}</div></div></div>
    <div class="note">For a date typed wrong. Expiry follows the node. The change is logged with your name and the time.</div>
    <form class="box" id="edd-form" data-record="${esc(r.recordId)}" novalidate>
      <div class="rows">
        <div class="r"><span class="k">Now</span><span class="v">${usDate(r.completedOn)} · ${r.expiresOn ? `expires ${usDate(r.expiresOn)}` : "does not expire"}</span><span class="e"></span></div>
        <label class="r"><span class="k">New date</span><span class="v"><input class="fin" type="date" name="completedOn" value="${esc(v)}" max="${today}" required></span><span class="e"></span></label>
        <div class="r"><span class="k">New expiry</span><span class="v" id="edd-exp">${exp ? usDate(exp) : r.expiresOn === null ? "Does not expire" : "Set from the date"}</span><span class="e"></span></div>
        <div class="r"><span class="k">Record</span><span class="v mono">${esc(r.recordId)}</span><span class="e"></span></div>
      </div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="edd-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>`;
}

/** Expiry preview as the date changes. Node 4 does not expire. */
export function expiryPreview(completedOn: string, nodeId: NodeId): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(completedOn)) return "Set from the date";
  const exp = expiresOn(completedOn, nodeId);
  return exp ? usDate(exp) : "Does not expire";
}
